import { StreamflowStation } from '../../types';
import store from '../../state';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const LAKE_ELEV_CODES = new Set(['00062', '62614', '62615', '62616', '72275']);

export class StreamflowModal {
  private container: HTMLElement;
  private chart: Chart | null = null;
  private currentStation: StreamflowStation | null = null;
  private rawCfsSeries: { time: Date; val: number }[] = [];
  private rawHeightSeries: { time: Date; val: number }[] = [];
  private rawElevSeries: { time: Date; val: number }[] = [];
  private rawTempSeries: { time: Date; val: number }[] = [];
  private rawStorageSeries: { time: Date; val: number }[] = [];
  private activeDaysWindow: number = 30; // 1, 5, 15, 30

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'streamflow-modal-container';
    this.container.style.display = 'none';
    this.container.style.position = 'fixed';
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.width = '100vw';
    this.container.style.height = '100vh';
    this.container.style.backgroundColor = 'rgba(0, 0, 0, 0.72)';
    this.container.style.backdropFilter = 'blur(10px)';
    (this.container.style as any).WebkitBackdropFilter = 'blur(10px)';
    this.container.style.zIndex = '9999';
    this.container.style.alignItems = 'center';
    this.container.style.justifyContent = 'center';

    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  public open(station: StreamflowStation): void {
    this.currentStation = station;
    this.rawCfsSeries = [];
    this.rawHeightSeries = [];
    this.rawElevSeries = [];
    this.rawTempSeries = [];
    this.rawStorageSeries = [];
    this.activeDaysWindow = 30;
    this.container.style.display = 'flex';
    this.render();
    void this.loadTrendData(station);
  }

  public close(): void {
    this.container.style.display = 'none';
    this.destroyChart();
    store.update({ streamflowSelectedStation: null });
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private render(): void {
    if (!this.currentStation) return;
    const s = this.currentStation;
    const agency = s.agency || 'USGS';

    const hasCfs = s.cfs !== null && s.cfs !== undefined;
    const hasHeight = s.height !== null && s.height !== undefined;
    const hasTemp = s.water_temp_f !== null && s.water_temp_f !== undefined;
    const hasStorage = s.storage_acre_ft !== null && s.storage_acre_ft !== undefined;
    const hasElev = s.elevation_ft !== null && s.elevation_ft !== undefined;
    const hasPctNorm = s.pct_normal !== null && s.pct_normal !== undefined;
    const hasPctCap = s.pct_capacity !== null && s.pct_capacity !== undefined;

    const isReservoir = s.site_type === 'reservoir' || hasStorage || hasElev;
    const headerIcon = isReservoir ? '🏞️' : '💧';

    const cfsDisplay = hasCfs ? `${Number(s.cfs).toLocaleString()} CFS` : null;
    const heightDisplay = hasHeight && !hasElev ? `${Number(s.height).toFixed(2)} FT` : null;
    const elevDisplay = hasElev ? `${Number(s.elevation_ft).toLocaleString(undefined, { maximumFractionDigits: 2 })} FT MSL` : null;
    const storageDisplay = hasStorage ? `${Math.round(Number(s.storage_acre_ft)).toLocaleString()} Ac-Ft` : null;
    const tempDisplay = hasTemp ? `${s.water_temp_f}°F (${s.water_temp_c}°C)` : null;

    const statusLabels: Record<string, { label: string; color: string }> = {
      low: { label: 'Much Below Normal (<25%)', color: '#D97706' },
      below_normal: { label: 'Below Normal (25-75%)', color: '#FBBF24' },
      normal: { label: 'Near Normal (75-125%)', color: '#34D399' },
      above_normal: { label: 'Above Normal (125-200%)', color: '#22D3EE' },
      high: { label: 'Much Above Normal (>200%)', color: '#60A5FA' },
      flood: { label: 'Extreme High / Flood Stage', color: '#C084FC' },
      unknown: { label: isReservoir ? 'Active Lake / Reservoir' : 'Active River Gauge', color: '#94A3B8' }
    };
    const stInfo = statusLabels[s.status] || statusLabels.unknown;

    let normSubtext = stInfo.label;
    if (isReservoir) {
      if (s.normal_storage_acre_ft) {
        normSubtext = `Normal: ${Math.round(Number(s.normal_storage_acre_ft)).toLocaleString()} Ac-Ft`;
      } else if (s.elev_diff_ft !== null && s.elev_diff_ft !== undefined) {
        const sign = Number(s.elev_diff_ft) >= 0 ? '+' : '';
        normSubtext = `${sign}${Number(s.elev_diff_ft).toFixed(2)} ft vs. normal`;
      }
    } else if (s.normal_cfs !== null && s.normal_cfs !== undefined) {
      normSubtext = `Normal: ${Number(s.normal_cfs).toLocaleString()} CFS`;
    }

    const externalHref = s.external_url || `https://waterdata.usgs.gov/monitoring-location/${s.id}/`;

    this.container.innerHTML = `
      <div style="background: #111827; border: 1px solid #374151; border-radius: 12px; width: 92%; max-width: 840px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.55); color: #F3F4F6; position: relative;">
        <button id="sf-modal-close" style="position: absolute; top: 16px; right: 16px; background: transparent; border: none; color: #9CA3AF; font-size: 24px; cursor: pointer;">&times;</button>

        <div style="display: flex; align-items: baseline; gap: 10px; margin-bottom: 4px; padding-right: 28px;">
          <h2 style="margin: 0; font-size: 20px; font-weight: 700; color: #60A5FA;">${headerIcon} ${s.name}</h2>
        </div>
        <div style="font-size: 13px; color: #9CA3AF; margin-bottom: 16px;">
          ${agency} ${isReservoir ? 'Lake / Reservoir' : 'River Gauge'}: <strong>${s.id}</strong> · Coordinates: ${s.lat.toFixed(4)}°, ${s.lon.toFixed(4)}°
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(145px, 1fr)); gap: 12px; margin-bottom: 20px; background: #1F2937; padding: 14px; border-radius: 8px;">
          ${storageDisplay ? `
          <div style="text-align: center;">
            <div style="font-size: 11px; text-transform: uppercase; color: #9CA3AF; font-weight: 600;">Storage Volume</div>
            <div style="font-size: 18px; font-weight: 800; color: #60A5FA;">${storageDisplay}</div>
            ${hasPctCap ? `<div style="font-size: 11px; color: #94A3B8; margin-top: 2px;">${Math.round(Number(s.pct_capacity))}% of Capacity</div>` : ''}
          </div>
          ` : ''}
          ${elevDisplay ? `
          <div style="text-align: center;">
            <div style="font-size: 11px; text-transform: uppercase; color: #9CA3AF; font-weight: 600;">Water Elevation</div>
            <div style="font-size: 18px; font-weight: 800; color: #A7F3D0;">${elevDisplay}</div>
          </div>
          ` : ''}
          ${cfsDisplay ? `
          <div style="text-align: center;">
            <div style="font-size: 11px; text-transform: uppercase; color: #9CA3AF; font-weight: 600;">${isReservoir ? 'Outflow / Release' : 'Discharge / Flow'}</div>
            <div style="font-size: 18px; font-weight: 800; color: #34D399;">${cfsDisplay}</div>
          </div>
          ` : ''}
          ${heightDisplay ? `
          <div style="text-align: center;">
            <div style="font-size: 11px; text-transform: uppercase; color: #9CA3AF; font-weight: 600;">Gage Stage</div>
            <div style="font-size: 18px; font-weight: 800; color: #FBBF24;">${heightDisplay}</div>
          </div>
          ` : ''}
          ${tempDisplay ? `
          <div style="text-align: center;">
            <div style="font-size: 11px; text-transform: uppercase; color: #9CA3AF; font-weight: 600;">Water Temp</div>
            <div style="font-size: 18px; font-weight: 800; color: #F472B6;">${tempDisplay}</div>
          </div>
          ` : ''}
          <div style="text-align: center;">
            <div style="font-size: 11px; text-transform: uppercase; color: #9CA3AF; font-weight: 600;">% of Normal</div>
            <div style="font-size: 18px; font-weight: 800; color: ${stInfo.color};">${hasPctNorm ? `${Math.round(Number(s.pct_normal))}%` : '—'}</div>
            <div style="font-size: 11px; color: #94A3B8; margin-top: 2px;">${normSubtext}</div>
          </div>
        </div>

        <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
          <div style="font-weight: 600; font-size: 14px; color: #D1D5DB; display: flex; align-items: center; gap: 8px;">
            <span>📈 Historical Trend Record</span>
            <span id="sf-chart-status" style="font-size: 12px; color: #9CA3AF; font-weight: 400;">Loading live 30-day record...</span>
          </div>

          <div style="display: flex; gap: 4px; background: #1F2937; padding: 3px; border-radius: 6px; border: 1px solid #374151;">
            <button class="sf-tf-btn" data-days="1" style="padding: 4px 8px; font-size: 11px; font-weight: 700; border-radius: 4px; border: none; background: transparent; color: #9CA3AF; cursor: pointer;">24H</button>
            <button class="sf-tf-btn" data-days="5" style="padding: 4px 8px; font-size: 11px; font-weight: 700; border-radius: 4px; border: none; background: transparent; color: #9CA3AF; cursor: pointer;">5D</button>
            <button class="sf-tf-btn" data-days="15" style="padding: 4px 8px; font-size: 11px; font-weight: 700; border-radius: 4px; border: none; background: transparent; color: #9CA3AF; cursor: pointer;">15D</button>
            <button class="sf-tf-btn active" data-days="30" style="padding: 4px 8px; font-size: 11px; font-weight: 700; border-radius: 4px; border: none; background: #3B82F6; color: #FFFFFF; cursor: pointer;">30D</button>
          </div>
        </div>

        <div style="position: relative; height: 260px; width: 100%; background: #111827; border-radius: 8px;">
          <canvas id="streamflow-trend-canvas"></canvas>
        </div>

        <div style="margin-top: 16px; text-align: right;">
          <a href="${externalHref}" target="_blank" rel="noopener noreferrer" style="color: #60A5FA; text-decoration: none; font-size: 13px; font-weight: 600;">View Official ${agency} Monitoring Page &rarr;</a>
        </div>
      </div>
    `;

    document.getElementById('sf-modal-close')?.addEventListener('click', () => this.close());
    this.setupTimeframeButtons();
  }

  private setupTimeframeButtons(): void {
    const btns = this.container.querySelectorAll('.sf-tf-btn');
    btns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const days = parseInt(target.getAttribute('data-days') || '30', 10);
        this.activeDaysWindow = days;

        btns.forEach(b => {
          (b as HTMLElement).style.background = 'transparent';
          (b as HTMLElement).style.color = '#9CA3AF';
        });
        target.style.background = '#3B82F6';
        target.style.color = '#FFFFFF';

        this.applyWindowFilterAndRender();
      });
    });
  }

  private ingestUsgsTimeSeries(timeSeries: any[]): void {
    for (const ts of timeSeries) {
      const varCd = ts?.variable?.variableCode?.[0]?.value;
      const values = ts?.values?.[0]?.value || [];
      for (const item of values) {
        const t = new Date(item.dateTime);
        const v = floatOrNull(item.value);
        if (v !== null && v !== -999999 && !isNaN(t.getTime())) {
          if (varCd === '00060') this.rawCfsSeries.push({ time: t, val: v });
          else if (varCd === '00065') this.rawHeightSeries.push({ time: t, val: v });
          else if (varCd === '00010') this.rawTempSeries.push({ time: t, val: Math.round((v * 1.8 + 32.0) * 10) / 10 });
          else if (varCd === '00054') this.rawStorageSeries.push({ time: t, val: v });
          else if (varCd === '72036') this.rawStorageSeries.push({ time: t, val: v * 1000.0 });
          else if (LAKE_ELEV_CODES.has(varCd)) this.rawElevSeries.push({ time: t, val: v });
        }
      }
    }
  }

  private async loadTrendData(station: StreamflowStation): Promise<void> {
    const statusEl = document.getElementById('sf-chart-status');
    const stationId = station.id;
    const isNumericUsgs = /^\d{8,15}$/.test(stationId);
    const paramCodes = '00060,00065,00010,00054,00062,62614,62615,62616,72036,72275';

    try {
      if (isNumericUsgs) {
        const ivUrl = `https://waterservices.usgs.gov/nwis/iv/?format=json&sites=${stationId}&period=P30D&parameterCd=${paramCodes}`;
        const resp = await fetch(ivUrl);
        if (resp.ok) {
          const data = await resp.json();
          this.ingestUsgsTimeSeries(data?.value?.timeSeries || []);
        }

        // Fallback to USGS Daily Values if IV has no observations (e.g. daily-reported lakes)
        if (
          this.rawCfsSeries.length === 0 &&
          this.rawStorageSeries.length === 0 &&
          this.rawElevSeries.length === 0 &&
          this.rawHeightSeries.length === 0
        ) {
          const dvUrl = `https://waterservices.usgs.gov/nwis/dv/?format=json&sites=${stationId}&period=P30D&parameterCd=${paramCodes}`;
          const dvResp = await fetch(dvUrl);
          if (dvResp.ok) {
            const dvData = await dvResp.json();
            this.ingestUsgsTimeSeries(dvData?.value?.timeSeries || []);
          }
        }
      }

      // Supplemental 30-day daily reservoir storage via NRCS AWDB BOR (e.g. Lake Mead 09421000, Flaming Gorge)
      if (
        this.rawStorageSeries.length === 0 &&
        (station.site_type === 'reservoir' || station.storage_acre_ft !== null) &&
        (isNumericUsgs || stationId.startsWith('BOR-'))
      ) {
        const borId = stationId.startsWith('BOR-') ? stationId.slice(4) : stationId;
        const endDt = new Date();
        const startDt = new Date(endDt.getTime() - 32 * 86400 * 1000);
        const fmt = (d: Date) => d.toISOString().slice(0, 10);
        const awdbUrl = `https://wcc.sc.egov.usda.gov/awdbRestApi/services/v1/data?stationTriplets=${encodeURIComponent(borId + ':*:BOR')}&elements=RESC&duration=DAILY&beginDate=${fmt(startDt)}&endDate=${fmt(endDt)}`;
        try {
          const awdbResp = await fetch(awdbUrl);
          if (awdbResp.ok) {
            const awdbList = await awdbResp.json();
            for (const entry of awdbList || []) {
              for (const blk of entry?.data || []) {
                for (const pt of blk?.values || []) {
                  const v = floatOrNull(pt?.value);
                  if (v !== null && pt?.date) {
                    const t = new Date(`${pt.date}T12:00:00Z`);
                    if (!isNaN(t.getTime())) {
                      this.rawStorageSeries.push({ time: t, val: v });
                    }
                  }
                }
              }
            }
          }
        } catch {
          // Optional supplemental trend source
        }
      }

      this.applyWindowFilterAndRender();
    } catch (e: any) {
      if (statusEl) statusEl.innerText = 'Daily summary snapshot available above';
      console.warn('Error loading hydrological trend:', e);
    }
  }

  private applyWindowFilterAndRender(): void {
    const statusEl = document.getElementById('sf-chart-status');
    const totalObs = Math.max(
      this.rawCfsSeries.length,
      this.rawStorageSeries.length,
      this.rawElevSeries.length,
      this.rawHeightSeries.length,
      this.rawTempSeries.length
    );
    if (totalObs === 0) {
      if (statusEl) {
        const ag = this.currentStation?.agency || 'USGS';
        statusEl.innerText = `Live ${ag} daily telemetry snapshot shown above`;
      }
      return;
    }

    const now = Date.now();
    const cutoffMs = now - (this.activeDaysWindow * 24 * 60 * 60 * 1000);

    const filterWithFallback = (arr: { time: Date; val: number }[]) => {
      const sub = arr.filter(d => d.time.getTime() >= cutoffMs);
      return sub.length > 0 ? sub : (this.activeDaysWindow <= 5 ? arr.slice(-Math.max(2, this.activeDaysWindow)) : sub);
    };

    const filteredCfs = filterWithFallback(this.rawCfsSeries);
    const filteredHeight = filterWithFallback(this.rawHeightSeries);
    const filteredElev = filterWithFallback(this.rawElevSeries);
    const filteredTemp = filterWithFallback(this.rawTempSeries);
    const filteredStorage = filterWithFallback(this.rawStorageSeries);

    const obsCount = Math.max(
      filteredCfs.length,
      filteredStorage.length,
      filteredElev.length,
      filteredHeight.length,
      filteredTemp.length
    );

    if (statusEl) {
      statusEl.innerText = `${this.activeDaysWindow}-Day Trend (${obsCount} observations)`;
    }

    this.renderChart(filteredCfs, filteredHeight, filteredElev, filteredTemp, filteredStorage);
  }

  private renderChart(
    cfsData: { time: Date; val: number }[],
    heightData: { time: Date; val: number }[],
    elevData: { time: Date; val: number }[],
    tempData: { time: Date; val: number }[],
    storageData: { time: Date; val: number }[]
  ): void {
    this.destroyChart();

    const canvas = document.getElementById('streamflow-trend-canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const levelSeries = elevData.length > 0 ? elevData : (cfsData.length === 0 && storageData.length === 0 ? heightData : []);
    const primarySeries =
      cfsData.length > 0 ? cfsData :
      storageData.length > 0 ? storageData :
      levelSeries.length > 0 ? levelSeries :
      tempData;
    if (primarySeries.length === 0) return;

    const showDateOnly = this.activeDaysWindow > 5 || primarySeries.length <= 35;
    const labels = primarySeries.map(d =>
      d.time.toLocaleDateString(
        undefined,
        showDateOnly
          ? { month: 'numeric', day: 'numeric' }
          : { month: 'numeric', day: 'numeric', hour: '2-digit' }
      )
    );

    const datasets: any[] = [];
    const scales: any = {
      x: {
        grid: { color: 'rgba(75, 85, 99, 0.3)' },
        ticks: { color: '#9CA3AF', maxTicksLimit: showDateOnly ? 10 : 8 }
      }
    };

    if (cfsData.length > 0) {
      datasets.push({
        label: 'Discharge (CFS)',
        data: cfsData.map(d => d.val),
        borderColor: '#34D399',
        backgroundColor: 'rgba(52, 211, 153, 0.1)',
        borderWidth: 2,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.2,
        yAxisID: 'y'
      });
      scales.y = {
        type: 'linear',
        display: true,
        position: 'left',
        grid: { color: 'rgba(75, 85, 99, 0.3)' },
        ticks: { color: '#34D399' },
        title: { display: true, text: 'Discharge (cfs)', color: '#34D399' }
      };
    }

    if (storageData.length > 0) {
      const axisKey = cfsData.length > 0 ? 'y2' : 'y';
      datasets.push({
        label: 'Reservoir Storage (Acre-Ft)',
        data: storageData.map(d => d.val),
        borderColor: '#60A5FA',
        backgroundColor: 'rgba(96, 165, 250, 0.12)',
        borderWidth: 2,
        fill: cfsData.length === 0,
        pointRadius: storageData.length <= 35 ? 2.5 : 0,
        pointHoverRadius: 5,
        tension: 0.2,
        yAxisID: axisKey
      });
      scales[axisKey] = {
        type: 'linear',
        display: true,
        position: cfsData.length > 0 ? 'right' : 'left',
        grid: { drawOnChartArea: cfsData.length === 0, color: 'rgba(75, 85, 99, 0.3)' },
        ticks: { color: '#60A5FA' },
        title: { display: true, text: 'Storage (Acre-Ft)', color: '#60A5FA' }
      };
    } else if (levelSeries.length > 0) {
      const axisKey = cfsData.length > 0 ? 'y2' : 'y';
      const labelTxt = elevData.length > 0 ? 'Water Surface Elevation (ft MSL)' : 'Gage Height (ft)';
      datasets.push({
        label: labelTxt,
        data: levelSeries.map(d => d.val),
        borderColor: '#38BDF8',
        backgroundColor: 'rgba(56, 189, 248, 0.12)',
        borderWidth: 2,
        fill: cfsData.length === 0,
        pointRadius: levelSeries.length <= 35 ? 2.5 : 0,
        pointHoverRadius: 5,
        tension: 0.2,
        yAxisID: axisKey
      });
      scales[axisKey] = {
        type: 'linear',
        display: true,
        position: cfsData.length > 0 ? 'right' : 'left',
        grid: { drawOnChartArea: cfsData.length === 0, color: 'rgba(75, 85, 99, 0.3)' },
        ticks: { color: '#38BDF8' },
        title: { display: true, text: elevData.length > 0 ? 'Elevation (ft)' : 'Stage (ft)', color: '#38BDF8' }
      };
    }

    if (tempData.length > 0) {
      const hasPrimaryLeft = cfsData.length > 0 || storageData.length > 0 || levelSeries.length > 0;
      const axisKey = hasPrimaryLeft ? 'y1' : 'y';
      datasets.push({
        label: 'Water Temp (°F)',
        data: tempData.map(d => d.val),
        borderColor: '#F472B6',
        backgroundColor: 'rgba(244, 114, 182, 0.1)',
        borderWidth: 2,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.2,
        yAxisID: axisKey
      });
      scales[axisKey] = {
        type: 'linear',
        display: true,
        position: hasPrimaryLeft ? 'right' : 'left',
        grid: { drawOnChartArea: !hasPrimaryLeft, color: 'rgba(75, 85, 99, 0.3)' },
        ticks: { color: '#F472B6' },
        title: { display: true, text: 'Water Temp (°F)', color: '#F472B6' }
      };
    }

    this.chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: scales,
        plugins: {
          legend: {
            display: datasets.length > 1,
            labels: { color: '#D1D5DB', font: { size: 11 } }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ${(ctx.parsed?.y ?? 0).toLocaleString()}`
            }
          }
        }
      }
    });
  }
}

function floatOrNull(v: any): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

export const streamflowModal = new StreamflowModal();
export default streamflowModal;
